/**
 * Operator availability per FieldType.
 *
 * The BE's CEL compiler validates the JSON tree at save time (and celpy enforces
 * type compatibility at compile time per DR §5.10) — the FE only chooses what to
 * EXPOSE. Use a conservative set per type. Operator names match
 * `server/modules/rules_engine/cel/operator_map.py::OPERATORS`.
 */

import type { FieldType } from './types';

export interface OperatorOption {
  name: string;
  label: string;
}

const COMMON_EQ_OPS: OperatorOption[] = [
  { name: '=', label: 'is' },
  { name: '!=', label: 'is not' },
];

const NULL_OPS: OperatorOption[] = [
  { name: 'null', label: 'is empty' },
  { name: 'notNull', label: 'is not empty' },
];

const STRING_OPS: OperatorOption[] = [
  ...COMMON_EQ_OPS,
  { name: 'in', label: 'is any of' },
  { name: 'notIn', label: 'is none of' },
  { name: 'beginsWith', label: 'begins with' },
  { name: 'endsWith', label: 'ends with' },
  { name: 'contains', label: 'contains' },
  ...NULL_OPS,
];

const NUMBER_OPS: OperatorOption[] = [
  ...COMMON_EQ_OPS,
  { name: '<', label: '<' },
  { name: '<=', label: '<=' },
  { name: '>', label: '>' },
  { name: '>=', label: '>=' },
  ...NULL_OPS,
];

const SELECT_OPS: OperatorOption[] = [
  ...COMMON_EQ_OPS,
  { name: 'in', label: 'is any of' },
  { name: 'notIn', label: 'is none of' },
  ...NULL_OPS,
];

// Multiselect operators are set-overlap on the path (which is itself a list),
// distinct from scalar `in` / `notIn` / `null` / `notNull`. BE names must
// match `_MULTISELECT_OPERATORS` in `rules_engine/cel/compiler.py`.
const MULTISELECT_OPS: OperatorOption[] = [
  { name: 'anyOf', label: 'is any of' },
  { name: 'noneOf', label: 'is none of' },
  { name: 'empty', label: 'is empty' },
  { name: 'notEmpty', label: 'is not empty' },
];

const BOOLEAN_OPS: OperatorOption[] = [...COMMON_EQ_OPS];

/** The operator list of a date field the comparison menu does not serve — a type-guarded date
 *  field (see `dateRelations.ts::fieldOffersOffset`). Those keep the plain Operator dropdown,
 *  which reads these labels. */
const DATE_OPS: OperatorOption[] = [
  ...COMMON_EQ_OPS,
  { name: '<', label: 'before' },
  { name: '<=', label: 'on or before' },
  { name: '>', label: 'after' },
  { name: '>=', label: 'on or after' },
  ...NULL_OPS,
];

/** The same comparisons as `DATE_OPS`, phrased for a time of day: "on or before 05:00" reads as a
 *  date. A `time` field always keeps the plain Operator dropdown, since no anchor applies to it. */
const TIME_OPS: OperatorOption[] = [
  ...COMMON_EQ_OPS,
  { name: '<', label: 'before' },
  { name: '<=', label: 'at or before' },
  { name: '>', label: 'after' },
  { name: '>=', label: 'at or after' },
  ...NULL_OPS,
];

/* --- anchored right-hand side ------------------------------------------------
 *
 * Date fields keep exactly the six comparisons above. What is polymorphic is the
 * *value* — the comparison's target — which is one of three shapes:
 *
 *   '2026-01-01'                                  a specific date (absolute)
 *   { anchor, precision? }                        a point: another instant itself
 *   { anchor, dir, n, unit, precision? }          an offset: n units before/after it
 *
 * `anchor` is `EVALUATED_AT_ANCHOR`, `TRIGGERED_AT_ANCHOR` or a date / datetime catalog
 * path. So `Claim Created / at least 30 Days before / When this rule runs` is the operator
 * `<=` over an offset value. Putting the shape in the value rather than in bespoke
 * operators is what keeps this list at six entries while covering every target.
 */

/** The whole-day units, each selecting a BE `<unit>_between` function. They are all
 *  date-granularity, which is why `hours` is not among them: at whole-day precision
 *  "in the last 6 hours" would silently mean six days. Must match
 *  `rules_engine/cel/operator_map.py::UNIT_TO_FUNCTION`. */
export const OFFSET_UNITS: OperatorOption[] = [
  { name: 'days', label: 'Days' },
  { name: 'business_days', label: 'Business Days' },
  { name: 'months', label: 'Months' },
];

/** The units an exact (to-the-minute) comparison can count in: the whole-day ones, which
 *  there keep the time of day, plus Hours, which only an exact comparison can honour. Must
 *  match the keys of `rules_engine/cel/operator_map.py::EXACT_UNIT_TO_FUNCTIONS`. */
export const EXACT_OFFSET_UNITS: OperatorOption[] = [{ name: 'hours', label: 'Hours' }, ...OFFSET_UNITS];

/** What a freshly-chosen offset relation seeds `unit` with. Days is the unit every
 *  temporal function shares a granularity with, and the one the commonest conditions are
 *  phrased in. */
export const DEFAULT_OFFSET_UNIT = OFFSET_UNITS[0].name;

/** Which side of the anchor the offset falls on. A separate field on the wire, never the
 *  sign of `n` — the BE rejects a negative count outright. Must match
 *  `rules_engine/cel/operator_map.py::OFFSET_DIRECTIONS`.
 *
 *  No longer a control the author sees: direction is one third of what a relation phrase
 *  names, so it is chosen with the comparison rather than three cells away from it. This
 *  list is what `validateConditionsTree` and the relation table check `dir` against. */
export const OFFSET_DIRECTIONS: OperatorOption[] = [
  { name: 'before', label: 'before' },
  { name: 'after', label: 'after' },
];

/** The anchor meaning "the moment this rule is evaluated". Not "now": for a
 *  deferred rule (a Custom-schedule notification, say) that instant is fire time,
 *  not event time, and naming it honestly is the point of naming it at all. Must match
 *  `rules_engine/cel/functions.py::EVALUATED_AT_ANCHOR`. */
export const EVALUATED_AT_ANCHOR = 'evaluated_at';

/** The anchor meaning "the moment the rule's triggering event happened" — the instant a
 *  deferred rule was scheduled from, which is what "nothing has happened since" is measured
 *  against. Only a rule that has a triggering event can use it: the BE refuses it on any
 *  other rule at save time, because there would be no event time to evaluate against.
 *  Must match `rules_engine/cel/functions.py::TRIGGERED_AT_ANCHOR`. */
export const TRIGGERED_AT_ANCHOR = 'triggered_at';

/** The precision marker an anchored value carries in `precision`. Never an author's
 *  toggle: the editor derives it from the two operand types (`dateRelations.ts::
 *  derivePrecision`). A value without the key compiles as whole-day, which is what every
 *  leaf stored before the marker existed means. Must match
 *  `rules_engine/cel/operator_map.py::PRECISIONS`. */
export const PRECISION_DAY = 'day';
export const PRECISION_EXACT = 'exact';

export type DatePrecision = typeof PRECISION_DAY | typeof PRECISION_EXACT;

/** A `user` field — a user's id — compares only with another `user` field, as `{anchor: <path>}`,
 *  or tests for presence: there is no literal an author could type. Must match
 *  `rules_engine/cel/operator_map.py::FIELD_COMPARISON_OPERATORS` for the comparisons. */
const USER_OPS: OperatorOption[] = [...COMMON_EQ_OPS, ...NULL_OPS];

/** The comparisons a `user` leaf makes against another `user` field. */
export const USER_COMPARISON_OPERATORS: ReadonlySet<string> = new Set(COMMON_EQ_OPS.map((operator) => operator.name));

const HIERARCHICAL_OPS: OperatorOption[] = [
  { name: '=', label: 'is' },
  { name: '!=', label: 'is not' },
];

export const OPERATORS_BY_TYPE: Record<FieldType, OperatorOption[]> = {
  string: STRING_OPS,
  multiline_string: STRING_OPS,
  number: NUMBER_OPS,
  monetary: NUMBER_OPS,
  boolean: BOOLEAN_OPS,
  date: DATE_OPS,
  datetime: DATE_OPS,
  time: TIME_OPS,
  select: SELECT_OPS,
  multiselect: MULTISELECT_OPS,
  us_state_select: SELECT_OPS,
  yes_no_na_unknown: SELECT_OPS,
  location: STRING_OPS,
  contact: STRING_OPS,
  hierarchical: HIERARCHICAL_OPS,
  user: USER_OPS,
  // Empty, and the only entry that is: a collection leaf has no scalar operand space at all, so
  // there is no comparison for an operator cell to offer — the quantifier lives in `match.mode`
  // and the predicates live on the collection's elements. react-querybuilder never reads it for
  // such a field either: a field declaring `matchModes` gets the match-mode control where the
  // operator cell would be. It is here because the map is exhaustive over `FieldType`, and an
  // omission falls back to `STRING_OPS`.
  collection: [],
};

/** What a field's operator cell offers. A type-guarded `user` field
 *  (`communication.variant.<channel>.user.id`) tests for presence alone: `_compile_variant_leaf`
 *  refuses an `{anchor}` value and a user has no literal, so a comparison could only fail on save. */
export const operatorsForField = (fieldType: FieldType, isVariantField: boolean): OperatorOption[] => {
  if (fieldType === 'user' && isVariantField) {
    return NULL_OPS;
  }
  return OPERATORS_BY_TYPE[fieldType] ?? OPERATORS_BY_TYPE.string;
};

// First offer wins, so a name two types phrase differently keeps the type-neutral wording: `<`
// is `<` (number) rather than `before` (date). That is the right answer for a caller with no type
// to choose on — a leaf whose field the catalog cannot resolve — and the fallback for one that
// does have a type but finds the operator absent from what it offers.
const OPERATOR_LABEL_BY_NAME = new Map<string, string>();
for (const operator of Object.values(OPERATORS_BY_TYPE).flat()) {
  if (!OPERATOR_LABEL_BY_NAME.has(operator.name)) {
    OPERATOR_LABEL_BY_NAME.set(operator.name, operator.label);
  }
}

/** The author-facing phrase for a wire operator name — `=` reads as `is`, `notNull` as
 *  `is not empty` — falling back to the name itself for anything no type offers (a hand-edited
 *  import, an operator that has since left the offer). */
export const operatorLabel = (name: string): string => OPERATOR_LABEL_BY_NAME.get(name) ?? name;
